import React, { useEffect, useRef, useState } from "react";
import { Calendar, Eye, EyeOff, Info, Lock, Pencil, Save, Shield } from "lucide-react";
import SuccessToast from "../components/SuccessToast";
import { parseInstant } from "../lib/dateTime";
import { normalizePhilippineMobile, validatePhilippineMobile } from "../lib/phoneNumber";
import { supabase } from "../supabaseClient";
import AdminTopBar from "./AdminTopBar";

const PROFILE_COLUMNS_WITH_PHONE = "id, email, full_name, role, created_at, phone";
const PROFILE_COLUMNS = "id, email, full_name, role, created_at";
const STRONG_PASSWORD_PATTERN = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

const CardHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-3 mb-5">
    <div className="w-9 h-9 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center shrink-0">
      <Icon size={16} />
    </div>
    <div>
      <h2 className="text-sm font-black uppercase tracking-wide text-gray-900">{title}</h2>
      <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>
    </div>
  </div>
);

const FieldLabel = ({ children }) => (
  <label className="block text-xs font-semibold text-gray-900 mb-1.5">{children}</label>
);

const PasswordField = ({ label, value, onChange, visible, onToggleVisible }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <div className="relative">
      <input
        type={visible ? "text" : "password"}
        value={value}
        onChange={onChange}
        className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 pr-10 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-brand-maroon"
      />
      <button
        type="button"
        onClick={onToggleVisible}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-800"
        aria-label={visible ? `Hide ${label}` : `Show ${label}`}
      >
        {visible ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  </div>
);

const formatRoleLabel = (value) =>
  String(value || "admin")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatMemberSince = (value) => {
  const parsed = parseInstant(value);
  if (!parsed) return "";
  return parsed.toLocaleDateString("en-PH", {
    timeZone: "Asia/Manila",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const isMissingPhoneColumnError = (error) => {
  const message = String(error?.message || error?.details || "").toLowerCase();
  return message.includes("phone") && (message.includes("column") || message.includes("does not exist") || message.includes("schema cache"));
};

const fetchOwnProfileRow = async (userId) => {
  const withPhone = await supabase
    .from("users")
    .select(PROFILE_COLUMNS_WITH_PHONE)
    .eq("id", userId)
    .maybeSingle();

  if (!withPhone.error) {
    return { row: withPhone.data, canPersistPhone: true };
  }

  if (!isMissingPhoneColumnError(withPhone.error)) {
    throw withPhone.error;
  }

  const withoutPhone = await supabase
    .from("users")
    .select(PROFILE_COLUMNS)
    .eq("id", userId)
    .maybeSingle();

  if (withoutPhone.error) throw withoutPhone.error;
  return { row: withoutPhone.data, canPersistPhone: false };
};

const AdminProfile = () => {
  const canPersistPhoneRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const savedProfileFormRef = useRef(null);
  const [changingPassword, setChangingPassword] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [errorToastMessage, setErrorToastMessage] = useState("");

  const [profile, setProfile] = useState({
    id: "",
    email: "",
    full_name: "",
    role: "admin",
    created_at: "",
  });

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone_number: "",
  });

  const [securityForm, setSecurityForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    next: false,
    confirm: false,
  });

  const showSuccessToast = (message) => {
    setToastMessage(message);
    window.setTimeout(() => setToastMessage(""), 2500);
  };

  const showErrorToast = (message) => {
    setErrorToastMessage(message);
    window.setTimeout(() => setErrorToastMessage(""), 3000);
  };

  useEffect(() => {
    const fetchCurrentAdmin = async () => {
      try {
        const {
          data: { session },
          error: authError,
        } = await supabase.auth.getSession();
        const user = session?.user;

        if (authError || !user?.id) {
          showErrorToast("Unable to load admin profile");
          return;
        }

        const { row: userRow, canPersistPhone } = await fetchOwnProfileRow(user.id);
        canPersistPhoneRef.current = canPersistPhone;

        const resolved = {
          id: user.id,
          email: userRow?.email || user.email || "",
          full_name: userRow?.full_name || user.user_metadata?.full_name || "",
          role: userRow?.role || "admin",
          created_at: userRow?.created_at || user.created_at || "",
        };

        setProfile(resolved);
        setFormData({
          full_name: resolved.full_name,
          email: resolved.email,
          phone_number:
            userRow?.phone ||
            user.user_metadata?.phone_number ||
            user.user_metadata?.phone ||
            "",
        });
      } catch (error) {
        console.error(error);
        showErrorToast("Unable to load admin profile");
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentAdmin();
  }, []);

  const handleSaveProfile = async () => {
    if (saving) return;

    const newFullName = formData.full_name.trim();
    const phoneError = validatePhilippineMobile(formData.phone_number);
    if (!newFullName) {
      showErrorToast("Full name is required.");
      return;
    }

    if (phoneError) {
      showErrorToast(phoneError);
      return;
    }

    const normalizedPhone = normalizePhilippineMobile(formData.phone_number);

    setSaving(true);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const user = session?.user;

      if (!user?.id) {
        throw new Error("Session expired. Please log in again.");
      }

      const profileFields = { full_name: newFullName };
      if (canPersistPhoneRef.current) {
        profileFields.phone = normalizedPhone || null;
      }

      const { error: updateUserRowError } = await supabase
        .from("users")
        .update(profileFields)
        .eq("id", user.id);

      if (updateUserRowError) {
        if (isMissingPhoneColumnError(updateUserRowError) && canPersistPhoneRef.current) {
          canPersistPhoneRef.current = false;
          const { error: retryError } = await supabase
            .from("users")
            .update({ full_name: newFullName })
            .eq("id", user.id);
          if (retryError) throw retryError;
        } else {
          throw updateUserRowError;
        }
      }

      const { error: authUpdateError } = await supabase.auth.updateUser({
        data: {
          full_name: newFullName,
          phone: normalizedPhone,
          phone_number: normalizedPhone,
        },
      });
      if (authUpdateError) throw authUpdateError;

      setProfile((prev) => ({
        ...prev,
        full_name: newFullName,
      }));
      setFormData((prev) => ({
        ...prev,
        full_name: newFullName,
        phone_number: normalizedPhone,
      }));
      savedProfileFormRef.current = {
        full_name: newFullName,
        phone_number: normalizedPhone,
      };
      setEditingProfile(false);
      window.dispatchEvent(new CustomEvent("admin-profile-updated"));
      showSuccessToast("Profile updated successfully");
    } catch (error) {
      console.error(error);
      showErrorToast(error.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const startEditingProfile = () => {
    savedProfileFormRef.current = {
      full_name: formData.full_name,
      phone_number: formData.phone_number,
    };
    setEditingProfile(true);
  };

  const cancelEditingProfile = () => {
    if (savedProfileFormRef.current) {
      setFormData((prev) => ({ ...prev, ...savedProfileFormRef.current }));
    }
    setEditingProfile(false);
  };

  const handleChangePassword = async () => {
    if (changingPassword) return;

    const currentPassword = securityForm.currentPassword;
    const newPassword = securityForm.newPassword.trim();
    const confirmPassword = securityForm.confirmPassword.trim();

    if (!currentPassword) {
      showErrorToast("Current password is required.");
      return;
    }

    if (!newPassword || !confirmPassword) {
      showErrorToast("Please fill in new and confirm password");
      return;
    }

    if (!STRONG_PASSWORD_PATTERN.test(newPassword)) {
      showErrorToast("New password must be at least 8 characters and include letters, numbers, and symbols.");
      return;
    }

    if (newPassword !== confirmPassword) {
      showErrorToast("New password and confirm password do not match");
      return;
    }

    setChangingPassword(true);

    try {
      const email = String(profile.email || "").trim();
      if (!email) {
        throw new Error("Session expired. Please log in again.");
      }

      const { error: reauthError } = await supabase.auth.signInWithPassword({
        email,
        password: currentPassword,
      });
      if (reauthError) {
        throw new Error("Current password is incorrect.");
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) throw error;

      setSecurityForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      showSuccessToast("Password updated successfully");
    } catch (error) {
      console.error(error);
      showErrorToast(error.message || "Failed to update password");
    } finally {
      setChangingPassword(false);
    }
  };

  const initials = (formData.full_name || profile.full_name || profile.email || "A")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  const memberSince = formatMemberSince(profile.created_at);

  if (loading) {
    return (
      <div className="bg-[#F3F2F1] min-h-screen">
        <AdminTopBar />
        <div className="p-4 sm:p-5 md:p-6 flex items-center justify-center">
          <p className="text-gray-400 font-medium">Loading account details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F3F2F1] min-h-screen">
      <AdminTopBar />
      <div className="p-4 sm:p-5 md:p-6">
        <SuccessToast message={toastMessage} />
        <SuccessToast message={errorToastMessage} variant="error" />

        <div className="mb-4 max-w-7xl mx-auto text-left">
          <h1 className="text-2xl md:text-[26px] font-black text-gray-900 tracking-tight uppercase italic">Account Details</h1>
        </div>

        <div className="max-w-4xl mx-auto mt-2 space-y-4">
          <section className="rounded-xl border border-gray-200 bg-white p-5 md:p-6 shadow-sm">
            <div className="flex items-center gap-4 mb-5">
              <div className="w-16 h-16 rounded-full bg-[#eb7be5] text-white font-bold text-xl flex items-center justify-center shrink-0">
                {initials || "A"}
              </div>
              <div className="min-w-0">
                <p className="text-base font-bold text-gray-900 truncate">
                  {formData.full_name || profile.full_name || "Admin"}
                </p>
                <p className="text-sm text-gray-500">Administrator Account</p>
                {memberSince && (
                  <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-gray-500">
                    <Calendar size={12} />
                    Member since {memberSince}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FieldLabel>Full Name</FieldLabel>
                <input
                  value={formData.full_name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, full_name: e.target.value }))}
                  readOnly={!editingProfile}
                  className={`h-10 w-full rounded-md border border-gray-300 px-3 text-sm outline-none ${
                    editingProfile
                      ? "bg-white text-gray-900 focus:ring-2 focus:ring-brand-maroon"
                      : "bg-gray-50 text-gray-700"
                  }`}
                />
              </div>

              <div>
                <FieldLabel>Phone Number</FieldLabel>
                <input
                  value={formData.phone_number}
                  onChange={(e) => setFormData((prev) => ({ ...prev, phone_number: e.target.value }))}
                  placeholder="09XXXXXXXXX or +639XXXXXXXXX"
                  readOnly={!editingProfile}
                  className={`h-10 w-full rounded-md border border-gray-300 px-3 text-sm outline-none ${
                    editingProfile
                      ? "bg-white text-gray-900 focus:ring-2 focus:ring-brand-maroon"
                      : "bg-gray-50 text-gray-700"
                  }`}
                />
              </div>

              <div>
                <FieldLabel>Email Address</FieldLabel>
                <div className="relative">
                  <input
                    type="email"
                    value={formData.email}
                    readOnly
                    className="h-10 w-full rounded-md border border-gray-300 bg-gray-50 px-3 pr-9 text-sm text-gray-700"
                  />
                  <Lock size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
                <p className="mt-1 text-xs text-gray-500">Email address cannot be changed.</p>
              </div>

              <div>
                <FieldLabel>Role</FieldLabel>
                <div className="relative">
                  <input
                    value={formatRoleLabel(profile.role)}
                    readOnly
                    className="h-10 w-full rounded-md border border-gray-300 bg-gray-50 px-3 pr-9 text-sm text-gray-700"
                  />
                  <Lock size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
                <p className="mt-1 text-xs text-gray-500">Role cannot be changed.</p>
              </div>
            </div>

            <div className="mt-5 flex flex-col-reverse sm:flex-row justify-end gap-2">
              {editingProfile ? (
                <>
                  <button
                    type="button"
                    onClick={cancelEditingProfile}
                    disabled={saving}
                    className="inline-flex items-center justify-center gap-2 border border-gray-300 bg-white text-gray-700 px-4 py-2.5 rounded-md text-xs font-bold uppercase hover:bg-gray-50 disabled:opacity-60 w-full sm:w-auto"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className="inline-flex items-center justify-center gap-2 bg-brand-maroon text-white px-4 py-2.5 rounded-md text-xs font-bold uppercase hover:bg-red-700 disabled:opacity-60 w-full sm:w-auto"
                  >
                    <Save size={14} />
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={startEditingProfile}
                  className="inline-flex items-center justify-center gap-2 bg-brand-maroon text-white px-4 py-2.5 rounded-md text-xs font-bold uppercase hover:bg-red-700 w-full sm:w-auto"
                >
                  <Pencil size={14} />
                  Edit
                </button>
              )}
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-5 md:p-6 shadow-sm">
            <CardHeader
              icon={Shield}
              title="Password & Security"
              subtitle="Update your password to keep your account secure."
            />

            <div className="space-y-4">
              <PasswordField
                label="Current Password"
                value={securityForm.currentPassword}
                onChange={(e) => setSecurityForm((prev) => ({ ...prev, currentPassword: e.target.value }))}
                visible={showPasswords.current}
                onToggleVisible={() => setShowPasswords((prev) => ({ ...prev, current: !prev.current }))}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <PasswordField
                  label="New Password"
                  value={securityForm.newPassword}
                  onChange={(e) => setSecurityForm((prev) => ({ ...prev, newPassword: e.target.value }))}
                  visible={showPasswords.next}
                  onToggleVisible={() => setShowPasswords((prev) => ({ ...prev, next: !prev.next }))}
                />
                <PasswordField
                  label="Confirm New Password"
                  value={securityForm.confirmPassword}
                  onChange={(e) => setSecurityForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                  visible={showPasswords.confirm}
                  onToggleVisible={() => setShowPasswords((prev) => ({ ...prev, confirm: !prev.confirm }))}
                />
              </div>
            </div>

            <div className="mt-4 flex items-start gap-2 rounded-md bg-red-50 px-3 py-2.5 text-xs text-red-700">
              <Info size={14} className="mt-0.5 shrink-0" />
              <p>
                <span className="font-semibold">Password requirements:</span> At least 8 characters long with a combination of letters, numbers, and symbols.
              </p>
            </div>

            <div className="mt-5 flex justify-end">
              <button
                onClick={handleChangePassword}
                disabled={changingPassword}
                className="inline-flex items-center justify-center gap-2 bg-brand-maroon text-white px-4 py-2.5 rounded-md text-xs font-bold uppercase hover:bg-red-700 disabled:opacity-60 w-full sm:w-auto"
              >
                <Lock size={14} />
                {changingPassword ? "Updating..." : "Update Password"}
              </button>
            </div>
          </section>

          <p className="pt-2 pb-1 text-center text-xs text-gray-400">
            © 2026 Echo Jham. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;
